// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-95 PR-C (ticket #1025) — the one dialog for changing an enrollment's lab list.
// Add, waive and restore each need a reason (design §5.1); the text is required when the code is
// "other" — the same rule the server and the table's CHECK enforce, said here first so nobody
// meets it as a 400. The host decides where the change goes: the create payload (no therapy yet)
// or POST lab-requirements (an existing enrollment).
import { useEffect, useState } from 'react'
import AppDialog from '../ui/AppDialog'
import AppButton from '../ui/AppButton'
import { AppTextField, SelectField } from '../ui/FormFields'
import { ANALYTES, analyteLabel } from '../../lib/analytes'
import type { LabReasonCode, LabRequirementItem } from '../../api/client'

export type LabRequirementAction = 'add' | 'waive' | 'restore'

export const LAB_REASONS: { value: LabReasonCode; label: string }[] = [
  { value: 'prescriber_request', label: 'Prescriber request' },
  { value: 'not_clinically_indicated', label: 'Not clinically indicated' },
  { value: 'done_elsewhere', label: 'Done elsewhere' },
  { value: 'contraindicated', label: 'Contraindicated' },
  { value: 'other', label: 'Other' },
]

export const labReasonLabel = (code?: string | null, text?: string | null) => {
  const l = LAB_REASONS.find(r => r.value === code)?.label ?? ''
  return text?.trim() ? (l ? `${l}: ${text.trim()}` : text.trim()) : l
}

const TITLE: Record<LabRequirementAction, string> = { add: 'Add a lab', waive: 'Waive a lab', restore: 'Restore a lab' }

export interface LabRequirementDialogProps {
  open: boolean
  action: LabRequirementAction
  /** waive / restore: the lab in question. add: ignored (the operator picks). */
  analyte?: string
  /** add: labs already on the list (required or waived), kept out of the picker. */
  exclude?: string[]
  busy?: boolean
  error?: string | null
  onClose: () => void
  onSubmit: (item: LabRequirementItem) => void
}

export default function LabRequirementDialog({ open, action, analyte, exclude = [], busy, error, onClose, onSubmit }: LabRequirementDialogProps) {
  const [pick, setPick] = useState('')
  const [code, setCode] = useState<LabReasonCode | ''>('')
  const [text, setText] = useState('')
  useEffect(() => { if (open) { setPick(''); setCode(''); setText('') } }, [open, action, analyte])

  const lab = action === 'add' ? pick : (analyte ?? '')
  const textNeeded = code === 'other'
  const ready = !!lab && !!code && (!textNeeded || text.trim().length > 0)
  const options = ANALYTES.filter(a => !exclude.includes(a.token)).map(a => ({ value: a.token, label: a.label }))

  return (
    <AppDialog open={open} title={TITLE[action]} onClose={onClose} busy={busy} maxWidth="xs"
      actions={<>
        <AppButton onClick={onClose} disabled={busy}>Cancel</AppButton>
        <AppButton variant="contained" disabled={!ready || busy}
          onClick={() => code && onSubmit({ analyte: lab, reason_code: code, reason_text: text.trim() || undefined })}>
          {TITLE[action].split(' ')[0]}
        </AppButton>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 6 }}>
        {action === 'add'
          ? <SelectField fullWidth size="small" label="Lab" required value={pick} onChange={setPick}
              options={[{ value: '', label: options.length ? 'Select a lab…' : 'Every lab is already on the list' }, ...options]} />
          : <div style={{ fontSize: 13 }}>Lab: <b>{analyteLabel(lab)}</b></div>}
        <SelectField fullWidth size="small" label="Reason" required value={code} onChange={v => setCode(v as LabReasonCode | '')}
          options={[{ value: '', label: 'Select a reason…' }, ...LAB_REASONS]} />
        <AppTextField fullWidth size="small" label={textNeeded ? 'Details (required)' : 'Details (optional)'} required={textNeeded}
          value={text} onChange={e => setText(e.target.value)} inputProps={{ maxLength: 600 }} multiline minRows={2} />
        {action === 'waive' && (
          <div style={{ fontSize: 11.5, color: 'var(--color-text-mute, #6b7280)' }}>
            A waived lab no longer holds up the Lab Tracker or Clinical Review. It stays on record with this reason and can be restored.
          </div>)}
        {error && <div role="alert" style={{ fontSize: 12.5, color: '#b91c1c' }}>{error}</div>}
      </div>
    </AppDialog>
  )
}
